import { HttpClient } from 'api/HttpClient';
import {
  LoanTerm,
  BorrowerEmploymentStatus,
  BorrowerEmployerState,
  BorrowerGovernmentPaygrade,
  BorrowerHomeImprovementEstimatesOrContractsCategoryEnum,
  BorrowerMilitaryPaygrade,
  OtherDocumentCategory,
  BorrowerLetterOfExplanationCategoryEnum,
  Borrower1099_CategoryEnum,
  BorrowerDriversLicenseBackMustBeInColorCategoryEnum,
  BorrowerClosingDisclosureCategoryEnum,
  BorrowerBranchOfService,
  BorrowerDirectDepositAuthorizationFormCategoryEnum,
  Status,
  Lender,
  PrequalificationVersion,
  KbaStatus,
} from 'enums/ApplicationsVariables';
import { BankAccountsStatus } from 'enums/BankAccountsStatus';
import { BorrowerOwnership } from 'api/UnderwritingApi';
import { VerificationState } from 'api/TruvApi';
import { IncomeType } from 'enums/IncomeType';

export interface DashboardAdditionalInfo {
  isAdditionalInfoTabRequired: boolean;
  isBorrowerCurrentEmploymentsRequired: boolean;
  isPartialBorrowerCurrentEmploymentsRequired: boolean;
  isBorrowerCurrentEmploymentsCompleted: boolean;
  isBorrowerPreviousEmploymentRequired: boolean;
  isBorrowerPreviousEmploymentCompleted: boolean;
  isBorrowerEmploymentStatusInformationRequired: boolean;
  isBorrowerEmploymentStatusInformationCompleted: boolean;
  isCoBorrowerCurrentEmploymentsRequired: boolean;
  isPartialCoBorrowerCurrentEmploymentsRequired: boolean;
  isCoBorrowerCurrentEmploymentsCompleted: boolean;
  isCoBorrowerPreviousEmploymentRequired: boolean;
  isCoBorrowerPreviousEmploymentCompleted: boolean;
  isCoBorrowerEmploymentStatusInformationRequired: boolean;
  isCoBorrowerEmploymentStatusInformationCompleted: boolean;
  isContractorEmploymentRequired: boolean;
  isContractorEmploymentCompleted: boolean;
  isMissingEmploymentInformationRequired: boolean;
  borrowerMissingEmploymentInformation: MissingEmploymentInformation[];
  coborrowerMissingEmploymentInformation: MissingEmploymentInformation[];
  isProsperView: boolean;
  isProsperJointRedirectView: boolean;
}

export interface MissingEmploymentInformation {
  index: number;
  incomeType: IncomeType;
  isCompleted: boolean;
}

export interface ApplicationDto {
  id: string;
  displayId: string;
  intermediaryId?: string;
  originalApplicationId?: string;
  borrowerId?: string;
  coborrowerIds: string[];
  status: {
    id: string;
    name: string;
  };
  labels: {
    id: string;
    name: string;
  }[];
  createdAt: Date;
  variables: ApplicationVariablesDto;
  productName: string;
  dashboardAdditionalInfo: DashboardAdditionalInfo;
  prequalificationVersion: PrequalificationVersion;
}

interface ApplicationVariablesWithoutDocumentVariablesDto {
  loan_amount?: number;
  sender_name?: string | null;
  consultant_sender_name?: string | null;
  consultant_sender_phone?: string | null;
  consultant_sender_email?: string | null;
  consultant_sender_position?: string | null;
  processor_sender_name?: string | null;
  processor_sender_phone?: string | null;
  processor_sender_email?: string | null;
  gross_loan?: number | null;
  rate?: number | null;
  apr?: number | null;
  loan_term?: LoanTerm | null;
  monthly_payment?: number | null;
  assigned_processor?: string | null;
  borrower_first_name?: string | null;
  borrower_last_name?: string | null;
  coborrower_first_name?: string | null;
  coborrower_last_name?: string | null;
  coborrower_email?: string | null;
  borrower_address_information_complete?: boolean | null;
  borrower_employment_information_complete?: boolean | null;
  coborrower_employment_information_complete?: boolean | null;
  borrower_payroll_provider_is_disabled?: boolean | null;
  coborrower_payroll_provider_is_disabled?: boolean | null;
  borrower_information_required?: boolean | null;
  coborrower_information_required?: boolean | null;
  funding_information_is_required?: boolean | null;
  is_there_a_non_borrowing_coowner?: boolean | null;
  homeownership_information_complete?: boolean | null;
  coowner_information_complete?: boolean | null;
  non_borrowing_coowner_first_name?: string | null;
  non_borrowing_coowner_last_name?: string | null;
  non_borrowing_coowner_middle_name?: string | null;
  borrower_work_order_address_verified?: Address | null;
  non_borrowing_coowner_email_address?: string | null;
  non_borrowing_coowner_ssn?: string | null;
  non_borrowing_coowner_mobile_phone?: string | null;
  non_borrowing_coowner_dob?: string | null;
  payroll_provider_borrower_verification_states?: VerificationState[];
  payroll_provider_coborrower_verification_states?: VerificationState[];
  payroll_provider_is_disabled?: boolean | null;
  funding_information_complete?: boolean | null;
  message_from_hfs?: string | null;
  borrower_email?: string | null;
  borrower_phone?: string | null;
  coborrower_phone?: string | null;
  processing_status_set_date?: string | null;
  borrower_w2_income_amount?: number | null;
  borrower_self_employed_income_amount?: number | null;
  borrower_homemaker_or_unemployed?: boolean | null;
  other_document_1_name?: string | null;
  other_document_2_name?: string | null;
  coborrower_w2_income_amount?: number | null;
  coborrower_self_employed_income_amount?: number | null;
  coborrower_net_va_disability_income_amount?: number | null;
  coborrower_net_military_pension_income_amount?: number | null;
  coborrower_employer_pension_income_amount?: number | null;
  coborrower_net_social_security_income_amount?: number | null;
  coborrower_net_rental_income_income_amount?: number | null;
  coborrower_homemaker_or_unemployed?: boolean | null;
  coborrower_other_income_amount?: number | null;
  bank_account_number?: string | null;
  bank_account_routing_number?: string | null;
  lender?: Lender;
  quilo_financial_institution_name?: string | null;
  borrower_id_document_verification_complete?: boolean | null;
  borrower_kba_required?: boolean | null;
  borrower_kba_status?: KbaStatus | null;
  coborrower_id_document_verification_complete?: boolean | null;
  coborrower_kba_required?: boolean | null;
  coborrower_kba_status?: KbaStatus | null;
  send_offer_2_to_consumer?: boolean | null;
  loan_term_2?: LoanTerm | null;
  lender_2?: Lender | null;
  autopay_2?: boolean | null;
  monthly_payment_2?: number | null;
  gross_loan_2?: number | null;
  rate_2?: number | null;
  apr_2?: number | null;
  message_from_hfs_show_on_portal?: boolean | null;
  hasAutomatedOffer?: boolean;
  hasPosAutomatedOffer?: boolean;
  multiple_loan_offers?: boolean | null;

  // Address variables
  borrower_home_address?: Address | null;
  move_in_date?: string | null;
  current_home_ownership?: BorrowerOwnership | null;
  borrower_previous_address_1?: Address | null;
  borrower_previous_street_address_1?: string | null;
  borrower_previous_city_1?: string | null;
  borrower_previous_state_1?: string | null;
  borrower_previous_zip_code_1?: string | null;
  borrower_previous_move_in_date_1?: string | null;
  coborrower_previous_street_address_1?: string | null;
  coborrower_previous_city_1?: string | null;
  coborrower_previous_state_1?: string | null;
  coborrower_previous_zip_code_1?: string | null;
  coborrower_previous_move_in_date_1?: string | null;

  // Employment variables
  borrower_employer_street_address?: Address | null;
  borrower_employment_status?: BorrowerEmploymentStatus | null;
  borrower_current_employer?: string | null;
  borrower_employer_address?: string | null;
  borrower_employer_phone?: string | null;
  borrower_employer_city?: string | null;
  borrower_employer_state?: BorrowerEmployerState | null;
  borrower_employer_zip_code?: string | null;
  borrower_position?: string | null;
  borrower_hire_date?: string | null;
  borrower_employer_income_amount?: number | null;
  borrower_branch_of_service?: BorrowerBranchOfService | null;
  borrower_branch_of_service_retired?: BorrowerBranchOfService | null;
  borrower_military_paygrade?: BorrowerMilitaryPaygrade | null;
  borrower_military_paygrade_retired?: BorrowerMilitaryPaygrade | null;
  borrower_government_paygrade?: BorrowerGovernmentPaygrade | null;
  borrower_retirement_date?: string | null;
  borrower_net_va_disability_income_amount?: number | null;
  borrower_net_military_pension_income_amount?: number | null;
  borrower_employer_pension_income_amount?: number | null;
  borrower_net_social_security_income_amount?: number | null;
  borrower_net_rental_income_income_amount?: number | null;
  borrower_other_income_amount?: number | null;
  bor_proof_of_income_required?: boolean | null;
  cob_proof_of_income_required?: boolean | null;

  borrower_previous_employment_status_1?: BorrowerEmploymentStatus | null;
  borrower_previous_employer_1?: string | null;
  borrower_previous_employer_1_address?: string | null;
  borrower_previous_employer_1_phone?: string | null;
  borrower_previous_employer_1_city?: string | null;
  borrower_previous_employer_1_state?: BorrowerEmployerState | null;
  borrower_previous_employer_1_zip_code?: string | null;
  borrower_previous_employer_1_position?: string | null;
  borrower_previous_employer_1_hire_date?: string | null;
  borrower_previous_employer_1_street_address?: Address | null;

  coborrower_employment_status?: BorrowerEmploymentStatus | null;
  coborrower_current_employer?: string | null;
  coborrower_employer_address?: string | null;
  coborrower_employer_phone?: string | null;
  coborrower_employer_city?: string | null;
  coborrower_employer_state?: BorrowerEmployerState | null;
  coborrower_employer_zip_code?: string | null;
  coborrower_position?: string | null;
  coborrower_hire_date?: string | null;
  coborrower_employer_income_amount?: number | null;
  coborrower_branch_of_service?: BorrowerBranchOfService | null;
  coborrower_branch_of_service_retired?: BorrowerBranchOfService | null;
  coborrower_military_paygrade?: BorrowerMilitaryPaygrade | null;
  coborrower_military_paygrade_retired?: BorrowerMilitaryPaygrade | null;
  coborrower_government_paygrade?: BorrowerGovernmentPaygrade | null;
  coborrower_retirement_date?: string | null;

  coborrower_previous_employment_status_1?: BorrowerEmploymentStatus | null;
  coborrower_previous_employer_1?: string | null;
  coborrower_previous_employer_1_address?: string | null;
  coborrower_previous_employer_1_phone?: string | null;
  coborrower_previous_employer_1_city?: string | null;
  coborrower_previous_employer_1_state?: BorrowerEmployerState | null;
  coborrower_previous_employer_1_zip_code?: string | null;
  coborrower_previous_employer_1_position?: string | null;
  coborrower_previous_employer_1_hire_date?: string | null;
  coborrower_previous_employer_1_street_address?: Address | null;

  borrower_employment_status_2?: BorrowerEmploymentStatus | null;
  borrower_current_employer_2?: string | null;
  borrower_employer_address_2?: string | null;
  borrower_employer_phone_2?: string | null;
  borrower_employer_city_2?: string | null;
  borrower_employer_state_2?: BorrowerEmployerState | null;
  borrower_employer_zip_code_2?: string | null;
  borrower_position_2?: string | null;
  borrower_hire_date_2?: string | null;
  borrower_employer_income_amount_2?: number | null;
  borrower_branch_of_service_2?: BorrowerBranchOfService | null;
  borrower_branch_of_service_retired_2?: BorrowerBranchOfService | null;
  borrower_military_paygrade_2?: BorrowerMilitaryPaygrade | null;
  borrower_military_paygrade_retired_2?: BorrowerMilitaryPaygrade | null;
  borrower_government_paygrade_2?: BorrowerGovernmentPaygrade | null;
  borrower_retirement_date_2?: string | null;

  borrower_employment_status_3?: BorrowerEmploymentStatus | null;
  borrower_current_employer_3?: string | null;
  borrower_employer_address_3?: string | null;
  borrower_employer_phone_3?: string | null;
  borrower_employer_city_3?: string | null;
  borrower_employer_state_3?: BorrowerEmployerState | null;
  borrower_employer_zip_code_3?: string | null;
  borrower_position_3?: string | null;
  borrower_hire_date_3?: string | null;
  borrower_employer_income_amount_3?: number | null;
  borrower_branch_of_service_3?: BorrowerBranchOfService | null;
  borrower_branch_of_service_retired_3?: BorrowerBranchOfService | null;
  borrower_military_paygrade_3?: BorrowerMilitaryPaygrade | null;
  borrower_military_paygrade_retired_3?: BorrowerMilitaryPaygrade | null;
  borrower_government_paygrade_3?: BorrowerGovernmentPaygrade | null;
  borrower_retirement_date_3?: string | null;

  coborrower_employment_status_2?: BorrowerEmploymentStatus | null;
  coborrower_current_employer_2?: string | null;
  coborrower_employer_address_2?: string | null;
  coborrower_employer_phone_2?: string | null;
  coborrower_employer_city_2?: string | null;
  coborrower_employer_state_2?: BorrowerEmployerState | null;
  coborrower_employer_zip_code_2?: string | null;
  coborrower_position_2?: string | null;
  coborrower_hire_date_2?: string | null;
  coborrower_employer_income_amount_2?: number | null;
  coborrower_branch_of_service_2?: BorrowerBranchOfService | null;
  coborrower_branch_of_service_retired_2?: BorrowerBranchOfService | null;
  coborrower_military_paygrade_2?: BorrowerMilitaryPaygrade | null;
  coborrower_military_paygrade_retired_2?: BorrowerMilitaryPaygrade | null;
  coborrower_government_paygrade_2?: BorrowerGovernmentPaygrade | null;
  coborrower_retirement_date_2?: string | null;

  coborrower_employment_status_3?: BorrowerEmploymentStatus | null;
  coborrower_current_employer_3?: string | null;
  coborrower_employer_address_3?: string | null;
  coborrower_employer_phone_3?: string | null;
  coborrower_employer_city_3?: string | null;
  coborrower_employer_state_3?: BorrowerEmployerState | null;
  coborrower_employer_zip_code_3?: string | null;
  coborrower_position_3?: string | null;
  coborrower_hire_date_3?: string | null;
  coborrower_employer_income_amount_3?: number | null;
  coborrower_branch_of_service_3?: BorrowerBranchOfService | null;
  coborrower_branch_of_service_retired_3?: BorrowerBranchOfService | null;
  coborrower_military_paygrade_3?: BorrowerMilitaryPaygrade | null;
  coborrower_military_paygrade_retired_3?: BorrowerMilitaryPaygrade | null;
  coborrower_government_paygrade_3?: BorrowerGovernmentPaygrade | null;
  coborrower_retirement_date_3?: string | null;
  funded_contractor_name?: string | null;
  funded_contractor_salesperson_name?: string | null;
  funded_contractor_salesperson_phone?: string | null;
  funded_contractor_salesperson_email?: string | null;
  borrower_experian_credit_score_v8?: number | null;
  coborrower_experian_credit_score_v8?: number | null;
  giact_validation_error_message?: string | null;
  bank_accounts_refresh_status?: BankAccountsStatus | null;
  borrower_financial_account_aggregation_attempt_complete?: boolean | null;
  autopay_required?: boolean | null;
  autopay?: boolean | null;
  autopay_discount?: number | null;
  accept_offer_pop_up_viewed?: boolean | null;
  loan_purpose?: string | null;
  contractor_information_complete?: boolean | null;
  prosper_accepted_offer_url?: string | null;
  prosper_joint_redirect?: boolean | null;
  maximum_transunion_vantage_score_v4?: number | null;
  giact_bank_account_verification_attempt_complete?: boolean | null;
  income_waterfall_applied?: boolean | null;
  borrower_income_waterfall_experian_visible?: boolean;
  coborrower_income_waterfall_experian_visible?: boolean;
  borrower_income_waterfall_payroll_provider_visible?: boolean;
  coborrower_income_waterfall_payroll_provider_visible?: boolean;
  borrower_income_waterfall_doc_upload_visible?: boolean;
  coborrower_income_waterfall_doc_upload_visible?: boolean;
  income_waterfall_completed?: boolean;
}

interface ApplicationDocumentVariables {
  borrower_w2_required?: boolean | null;
  borrower_w2_status?: Status | null;
  borrower_w2_category?: Borrower1099_CategoryEnum | null;
  borrower_w2_name?: string | null;
  borrower_most_recent_pay_stubs_required?: boolean | null;
  borrower_most_recent_pay_stubs_status?: Status | null;
  borrower_most_recent_pay_stubs_category?: Borrower1099_CategoryEnum | null;
  borrower_most_recent_pay_stubs_name?: string | null;
  borrower_2nd_most_recent_pay_stubs_required?: boolean | null;
  borrower_2nd_most_recent_pay_stubs_status?: Status | null;
  borrower_2nd_most_recent_pay_stubs_category?: Borrower1099_CategoryEnum | null;
  borrower_2nd_most_recent_pay_stubs_name?: string | null;
  borrower_most_recent_personal_tax_returns_required?: boolean | null;
  borrower_most_recent_personal_tax_returns_status?: Status | null;
  borrower_most_recent_personal_tax_returns_category?: Borrower1099_CategoryEnum | null;
  borrower_most_recent_personal_tax_returns_name?: string | null;
  borrower_2nd_most_recent_personal_tax_returns_required?: boolean | null;
  borrower_2nd_most_recent_personal_tax_returns_status?: Status | null;
  borrower_2nd_most_recent_personal_tax_returns_category?: Borrower1099_CategoryEnum | null;
  borrower_2nd_most_recent_personal_tax_returns_name?: string | null;
  borrower_most_recent_business_tax_returns_required?: boolean | null;
  borrower_most_recent_business_tax_returns_status?: Status | null;
  borrower_most_recent_business_tax_returns_category?: Borrower1099_CategoryEnum | null;
  borrower_most_recent_business_tax_returns_name?: string | null;
  borrower_2nd_most_recent_business_tax_returns_required?: boolean | null;
  borrower_2nd_most_recent_business_tax_returns_status?: Status | null;
  borrower_2nd_most_recent_business_tax_returns_category?: Borrower1099_CategoryEnum | null;
  borrower_2nd_most_recent_business_tax_returns_name?: string | null;
  borrower_va_disability_awards_letter_required?: boolean | null;
  borrower_va_disability_awards_letter_status?: Status | null;
  borrower_va_disability_awards_letter_category?: Borrower1099_CategoryEnum | null;
  borrower_va_disability_awards_letter_name?: string | null;
  borrower_military_pension_awards_letter_required?: boolean | null;
  borrower_military_pension_awards_letter_status?: Status | null;
  borrower_military_pension_awards_letter_category?: Borrower1099_CategoryEnum | null;
  borrower_military_pension_awards_letter_name?: string | null;
  borrower_social_security_awards_letter_required?: boolean | null;
  borrower_social_security_awards_letter_status?: Status | null;
  borrower_social_security_awards_letter_category?: Borrower1099_CategoryEnum | null;
  borrower_social_security_awards_letter_name?: string | null;
  borrower_employer_pension_letter_required?: boolean | null;
  borrower_employer_pension_letter_status?: Status | null;
  borrower_employer_pension_letter_category?: Borrower1099_CategoryEnum | null;
  borrower_employer_pension_letter_name?: string | null;
  borrower_rental_or_lease_agreement_required?: boolean | null;
  borrower_rental_or_lease_agreement_status?: Status | null;
  borrower_rental_or_lease_agreement_category?: Borrower1099_CategoryEnum | null;
  borrower_rental_or_lease_agreement_name?: string | null;
  borrower_1099_required?: boolean | null;
  borrower_1099_status?: Status | null;
  borrower_1099_category?: Borrower1099_CategoryEnum | null;
  borrower_1099_name?: string | null;
  borrower_profit_and_loss_required?: boolean | null;
  borrower_profit_and_loss_status?: Status | null;
  borrower_profit_and_loss_category?: Borrower1099_CategoryEnum | null;
  borrower_profit_and_loss_name?: string | null;
  borrower_tax_return_extension_required?: boolean | null;
  borrower_tax_return_extension_status?: Status | null;
  borrower_tax_return_extension_category?: Borrower1099_CategoryEnum | null;
  borrower_tax_return_extension_name?: string | null;
  borrower_drivers_license_required?: boolean | null;
  borrower_drivers_license_status?: Status | null;
  borrower_drivers_license_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  borrower_drivers_license_name?: string | null;
  borrower_passport_required?: boolean | null;
  borrower_passport_status?: Status | null;
  borrower_passport_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  borrower_passport_name?: string | null;
  borrower_social_security_card_required?: boolean | null;
  borrower_social_security_card_status?: Status | null;
  borrower_social_security_card_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  borrower_social_security_card_name?: string | null;
  borrower_green_card_required?: boolean | null;
  borrower_green_card_status?: Status | null;
  borrower_green_card_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  borrower_green_card_name?: string | null;
  borrower_monthly_mortgage_statement_required?: boolean | null;
  borrower_monthly_mortgage_statement_status?: Status | null;
  borrower_monthly_mortgage_statement_category?: BorrowerClosingDisclosureCategoryEnum | null;
  borrower_monthly_mortgage_statement_name?: string | null;
  borrower_closing_disclosure_required?: boolean | null;
  borrower_closing_disclosure_status?: Status | null;
  borrower_closing_disclosure_category?: BorrowerClosingDisclosureCategoryEnum | null;
  borrower_closing_disclosure_name?: string | null;
  borrower_deed_required?: boolean | null;
  borrower_deed_status?: Status | null;
  borrower_deed_category?: BorrowerClosingDisclosureCategoryEnum | null;
  borrower_deed_name?: string | null;
  borrower_utility_bill_required?: boolean | null;
  borrower_utility_bill_status?: Status | null;
  borrower_utility_bill_category?: BorrowerClosingDisclosureCategoryEnum | null;
  borrower_utility_bill_name?: string | null;
  borrower_home_improvement_estimates_or_contracts_required?: boolean | null;
  borrower_home_improvement_estimates_or_contracts_status?: Status | null;
  borrower_home_improvement_estimates_or_contracts_category?: BorrowerHomeImprovementEstimatesOrContractsCategoryEnum | null;
  borrower_home_improvement_estimates_or_contracts_name?: string | null;
  borrower_voided_check_required?: boolean | null;
  borrower_voided_check_status?: Status | null;
  borrower_voided_check_category?: BorrowerDirectDepositAuthorizationFormCategoryEnum | null;
  borrower_voided_check_name?: string | null;
  borrower_screenshot_of_online_banking_required?: boolean | null;
  borrower_screenshot_of_online_banking_status?: Status | null;
  borrower_screenshot_of_online_banking_category?: BorrowerDirectDepositAuthorizationFormCategoryEnum | null;
  borrower_screenshot_of_online_banking_name?: string | null;
  borrower_direct_deposit_authorization_form_required?: boolean | null;
  borrower_direct_deposit_authorization_form_status?: Status | null;
  borrower_direct_deposit_authorization_form_category?: BorrowerDirectDepositAuthorizationFormCategoryEnum | null;
  borrower_direct_deposit_authorization_form_name?: string | null;
  borrower_letter_of_explanation_required?: boolean | null;
  borrower_letter_of_explanation_status?: Status | null;
  borrower_letter_of_explanation_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  borrower_letter_of_explanation_name?: string | null;
  borrower_cell_phone_bill_required?: boolean | null;
  borrower_cell_phone_bill_category?: OtherDocumentCategory | null;
  borrower_cell_phone_bill_name?: string | null;
  borrower_cell_phone_bill_status?: Status | null;
  coborrower_cell_phone_bill_required?: boolean | null;
  coborrower_cell_phone_bill_category?: OtherDocumentCategory | null;
  coborrower_cell_phone_bill_name?: string | null;
  coborrower_cell_phone_bill_status?: Status | null;
  coborrower_w2_required?: boolean | null;
  coborrower_w2_status?: Status | null;
  coborrower_w2_category?: Borrower1099_CategoryEnum | null;
  coborrower_w2_name?: string | null;
  coborrower_most_recent_pay_stubs_required?: boolean | null;
  coborrower_most_recent_pay_stubs_status?: Status | null;
  coborrower_most_recent_pay_stubs_category?: Borrower1099_CategoryEnum | null;
  coborrower_most_recent_pay_stubs_name?: string | null;
  coborrower_2nd_most_recent_pay_stubs_required?: boolean | null;
  coborrower_2nd_most_recent_pay_stubs_status?: Status | null;
  coborrower_2nd_most_recent_pay_stubs_category?: Borrower1099_CategoryEnum | null;
  coborrower_2nd_most_recent_pay_stubs_name?: string | null;
  coborrower_most_recent_personal_tax_returns_required?: boolean | null;
  coborrower_most_recent_personal_tax_returns_status?: Status | null;
  coborrower_most_recent_personal_tax_returns_category?: Borrower1099_CategoryEnum | null;
  coborrower_most_recent_personal_tax_returns_name?: string | null;
  coborrower_2nd_most_recent_personal_tax_returns_required?: boolean | null;
  coborrower_2nd_most_recent_personal_tax_returns_status?: Status | null;
  coborrower_2nd_most_recent_personal_tax_returns_category?: Borrower1099_CategoryEnum | null;
  coborrower_2nd_most_recent_personal_tax_returns_name?: string | null;
  coborrower_most_recent_business_tax_returns_required?: boolean | null;
  coborrower_most_recent_business_tax_returns_status?: Status | null;
  coborrower_most_recent_business_tax_returns_category?: Borrower1099_CategoryEnum | null;
  coborrower_most_recent_business_tax_returns_name?: string | null;
  coborrower_2nd_most_recent_business_tax_returns_required?: boolean | null;
  coborrower_2nd_most_recent_business_tax_returns_status?: Status | null;
  coborrower_2nd_most_recent_business_tax_returns_category?: Borrower1099_CategoryEnum | null;
  coborrower_2nd_most_recent_business_tax_returns_name?: string | null;
  coborrower_va_disability_awards_letter_required?: boolean | null;
  coborrower_va_disability_awards_letter_status?: Status | null;
  coborrower_va_disability_awards_letter_category?: Borrower1099_CategoryEnum | null;
  coborrower_va_disability_awards_letter_name?: string | null;
  coborrower_military_pension_awards_letter_required?: boolean | null;
  coborrower_military_pension_awards_letter_status?: Status | null;
  coborrower_military_pension_awards_letter_category?: Borrower1099_CategoryEnum | null;
  coborrower_military_pension_awards_letter_name?: string | null;
  coborrower_social_security_awards_letter_required?: boolean | null;
  coborrower_social_security_awards_letter_status?: Status | null;
  coborrower_social_security_awards_letter_category?: Borrower1099_CategoryEnum | null;
  coborrower_social_security_awards_letter_name?: string | null;
  coborrower_employer_pension_letter_required?: boolean | null;
  coborrower_employer_pension_letter_status?: Status | null;
  coborrower_employer_pension_letter_category?: Borrower1099_CategoryEnum | null;
  coborrower_employer_pension_letter_name?: string | null;
  coborrower_rental_or_lease_agreement_required?: boolean | null;
  coborrower_rental_or_lease_agreement_status?: Status | null;
  coborrower_rental_or_lease_agreement_category?: Borrower1099_CategoryEnum | null;
  coborrower_rental_or_lease_agreement_name?: string | null;
  coborrower_1099_required?: boolean | null;
  coborrower_1099_status?: Status | null;
  coborrower_1099_category?: Borrower1099_CategoryEnum | null;
  coborrower_1099_name?: string | null;
  coborrower_profit_and_loss_required?: boolean | null;
  coborrower_profit_and_loss_status?: Status | null;
  coborrower_profit_and_loss_category?: Borrower1099_CategoryEnum | null;
  coborrower_profit_and_loss_name?: string | null;
  coborrower_tax_return_extension_required?: boolean | null;
  coborrower_tax_return_extension_status?: Status | null;
  coborrower_tax_return_extension_category?: Borrower1099_CategoryEnum | null;
  coborrower_tax_return_extension_name?: string | null;
  coborrower_drivers_license_required?: boolean | null;
  coborrower_drivers_license_status?: Status | null;
  coborrower_drivers_license_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  coborrower_drivers_license_name?: string | null;
  coborrower_passport_required?: boolean | null;
  coborrower_passport_status?: Status | null;
  coborrower_passport_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  coborrower_passport_name?: string | null;
  coborrower_social_security_card_required?: boolean | null;
  coborrower_social_security_card_status?: Status | null;
  coborrower_social_security_card_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  coborrower_social_security_card_name?: string | null;
  coborrower_green_card_required?: boolean | null;
  coborrower_green_card_status?: Status | null;
  coborrower_green_card_category?: BorrowerDriversLicenseBackMustBeInColorCategoryEnum | null;
  coborrower_green_card_name?: string | null;
  coborrower_utility_bill_required?: boolean | null;
  coborrower_utility_bill_status?: Status | null;
  coborrower_utility_bill_category?: BorrowerClosingDisclosureCategoryEnum | null;
  coborrower_utility_bill_name?: string | null;
  coborrower_letter_of_explanation_required?: boolean | null;
  coborrower_letter_of_explanation_status?: Status | null;
  coborrower_letter_of_explanation_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  coborrower_letter_of_explanation_name?: string | null;
  other_document_1_required?: boolean | null;
  other_document_1_status?: Status | null;
  other_document_1_category?: OtherDocumentCategory | null;
  other_document_1_name?: string | null;
  other_document_2_required?: boolean | null;
  other_document_2_status?: Status | null;
  other_document_2_category?: OtherDocumentCategory | null;
  other_document_2_name?: string | null;
  other_document_3_required?: boolean | null;
  other_document_3_status?: Status | null;
  other_document_3_category?: OtherDocumentCategory | null;
  other_document_3_name?: string | null;
  other_document_4_required?: boolean | null;
  other_document_4_status?: Status | null;
  other_document_4_category?: OtherDocumentCategory | null;
  other_document_4_name?: string | null;
  other_document_5_required?: boolean | null;
  other_document_5_status?: Status | null;
  other_document_5_category?: OtherDocumentCategory | null;
  other_document_5_name?: string | null;
  other_document_6_required?: boolean | null;
  other_document_6_status?: Status | null;
  other_document_6_category?: OtherDocumentCategory | null;
  other_document_6_name?: string | null;
  other_document_7_required?: boolean | null;
  other_document_7_status?: Status | null;
  other_document_7_category?: OtherDocumentCategory | null;
  other_document_7_name?: string | null;
  other_document_8_required?: boolean | null;
  other_document_8_status?: Status | null;
  other_document_8_category?: OtherDocumentCategory | null;
  other_document_8_name?: string | null;
  other_document_9_required?: boolean | null;
  other_document_9_status?: Status | null;
  other_document_9_category?: OtherDocumentCategory | null;
  other_document_9_name?: string | null;
  other_document_10_required?: boolean | null;
  other_document_10_status?: Status | null;
  other_document_10_category?: OtherDocumentCategory | null;
  other_document_10_name?: string | null;
  borrower_other_income_1_required?: boolean | null;
  borrower_other_income_1_status?: Status | null;
  borrower_other_income_1_category?: Borrower1099_CategoryEnum | null;
  borrower_other_income_1_name?: string | null;
  borrower_other_income_2_required?: boolean | null;
  borrower_other_income_2_status?: Status | null;
  borrower_other_income_2_category?: Borrower1099_CategoryEnum | null;
  borrower_other_income_2_name?: string | null;
  borrower_other_income_3_required?: boolean | null;
  borrower_other_income_3_status?: Status | null;
  borrower_other_income_3_category?: Borrower1099_CategoryEnum | null;
  borrower_other_income_3_name?: string | null;
  coborrower_other_income_1_required?: boolean | null;
  coborrower_other_income_1_status?: Status | null;
  coborrower_other_income_1_category?: Borrower1099_CategoryEnum | null;
  coborrower_other_income_1_name?: string | null;
  coborrower_other_income_2_required?: boolean | null;
  coborrower_other_income_2_status?: Status | null;
  coborrower_other_income_2_category?: Borrower1099_CategoryEnum | null;
  coborrower_other_income_2_name?: string | null;
  coborrower_other_income_3_required?: boolean | null;
  coborrower_other_income_3_status?: Status | null;
  coborrower_other_income_3_category?: Borrower1099_CategoryEnum | null;
  coborrower_other_income_3_name?: string | null;
  other_home_verification_document_required?: boolean | null;
  other_home_verification_document_status?: Status | null;
  other_home_verification_document_category?: BorrowerClosingDisclosureCategoryEnum | null;
  other_home_verification_document_name?: string | null;
  other_home_verification_document_2_required?: boolean | null;
  other_home_verification_document_2_status?: Status | null;
  other_home_verification_document_2_category?: BorrowerClosingDisclosureCategoryEnum | null;
  other_home_verification_document_2_name?: string | null;
  other_home_verification_document_3_required?: boolean | null;
  other_home_verification_document_3_status?: Status | null;
  other_home_verification_document_3_category?: BorrowerClosingDisclosureCategoryEnum | null;
  other_home_verification_document_3_name?: string | null;
  other_funding_document_required?: boolean | null;
  other_funding_document_status?: Status | null;
  other_funding_document_category?: BorrowerDirectDepositAuthorizationFormCategoryEnum | null;
  other_funding_document_name?: string | null;
  other_funding_document_2_required?: boolean | null;
  other_funding_document_2_status?: Status | null;
  other_funding_document_2_category?: BorrowerDirectDepositAuthorizationFormCategoryEnum | null;
  other_funding_document_2_name?: string | null;
  other_funding_document_3_required?: boolean | null;
  other_funding_document_3_status?: Status | null;
  other_funding_document_3_category?: BorrowerDirectDepositAuthorizationFormCategoryEnum | null;
  other_funding_document_3_name?: string | null;
  other_contractor_document_required?: boolean | null;
  other_contractor_document_status?: Status | null;
  other_contractor_document_category?: BorrowerHomeImprovementEstimatesOrContractsCategoryEnum | null;
  other_contractor_document_name?: string | null;
  other_contractor_document_2_required?: boolean | null;
  other_contractor_document_2_status?: Status | null;
  other_contractor_document_2_category?: BorrowerHomeImprovementEstimatesOrContractsCategoryEnum | null;
  other_contractor_document_2_name?: string | null;
  other_contractor_document_3_required?: boolean | null;
  other_contractor_document_3_status?: Status | null;
  other_contractor_document_3_category?: BorrowerHomeImprovementEstimatesOrContractsCategoryEnum | null;
  other_contractor_document_3_name?: string | null;
  borrower_misc_other_document_1_required?: boolean | null;
  borrower_misc_other_document_1_status?: Status | null;
  borrower_misc_other_document_1_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  borrower_misc_other_document_1_name?: string | null;
  coborrower_misc_other_document_1_required?: boolean | null;
  coborrower_misc_other_document_1_status?: Status | null;
  coborrower_misc_other_document_1_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  coborrower_misc_other_document_1_name?: string | null;
  borrower_misc_other_document_2_required?: boolean | null;
  borrower_misc_other_document_2_status?: Status | null;
  borrower_misc_other_document_2_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  borrower_misc_other_document_2_name?: string | null;
  coborrower_misc_other_document_2_required?: boolean | null;
  coborrower_misc_other_document_2_status?: Status | null;
  coborrower_misc_other_document_2_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  coborrower_misc_other_document_2_name?: string | null;
  borrower_misc_other_document_3_required?: boolean | null;
  borrower_misc_other_document_3_status?: Status | null;
  borrower_misc_other_document_3_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  borrower_misc_other_document_3_name?: string | null;
  coborrower_misc_other_document_3_required?: boolean | null;
  coborrower_misc_other_document_3_status?: Status | null;
  coborrower_misc_other_document_3_category?: BorrowerLetterOfExplanationCategoryEnum | null;
  coborrower_misc_other_document_3_name?: string | null;
  borrower_bank_statement_required?: string | null;
  borrower_bank_statement_category?: BorrowerDirectDepositAuthorizationFormCategoryEnum | null;
  borrower_bank_statement_status?: Status | null;
  borrower_bank_statement_name?: string | null;
}

export interface Address {
  city?: null | string;
  country?: null | string;
  state_or_province?: null | string;
  street_name?: null | string;
  street_number?: null | string;
  zip_or_postal_code?: null | string;
  unit_number?: null | string;
  sublocality?: null | string;
}

export interface ApplicationVariablesDto
  extends ApplicationVariablesWithoutDocumentVariablesDto,
    ApplicationDocumentVariables {}

export default class ApplicationApi extends HttpClient {
  public constructor() {
    super(process.env.REACT_APP_API_URL + '/applications' || '');
  }

  public async getActiveApplication(isAbbreviated: boolean = false): Promise<{
    borrowerApplication?: ApplicationDto;
    hasCobborowerApplication: boolean;
  }> {
    const params = new URLSearchParams();

    params.append('isAbbreviated', `${isAbbreviated}`);

    return this.instance.get('', { params: params }).then((response) => response.data);
  }

  public async updateApplicationLastActivityDate(id: string): Promise<{ application: ApplicationDto }> {
    return this.instance.put(`/${id}/updateLastActivityDate`).then((response) => response.data);
  }

  public async updateLoanPortalAccessTime(id: string): Promise<{ application: ApplicationDto }> {
    return this.instance.put(`/${id}/updateLoanPortalAccessTime`).then((response) => response.data);
  }

  public async markMessageFromHfsAsRead(id: string): Promise<{ application: ApplicationDto }> {
    return this.instance.put(`/${id}/markMessageFromHfsAsRead`).then((response) => response.data);
  }

  public async storeProsperJointRedirectDatetime(id: string): Promise<{ prosperJointRedirectUrl: string }> {
    return this.instance.put(`/${id}/prosperJointRedirect`).then((response) => response.data);
  }
}

export const applicationApi = new ApplicationApi();
